import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { MY_IABLUE_ASSISTANT_ID } from '../lib/constants';
import { PROMPT_DRAFT_QUERY_KEY } from './usePromptDraft';
import type {
  PromptVersionResponse,
  PromptVersionsListResponse,
  PromptPreviewResponse,
  PromptDraftResponse,
} from '@/types/api/prompt-versions';

const VERSIONS_QUERY_KEY = ['prompt-versions', MY_IABLUE_ASSISTANT_ID];

/**
 * Hook to fetch paginated list of published prompt versions
 */
export function usePromptVersions(page: number = 1, limit: number = 20) {
  return useQuery<PromptVersionsListResponse>({
    queryKey: [...VERSIONS_QUERY_KEY, page, limit],
    queryFn: async () => {
      const { data } = await api.get<PromptVersionsListResponse>(
        `/assistants/${MY_IABLUE_ASSISTANT_ID}/prompt/versions`,
        { params: { page, limit } }
      );
      return data;
    },
  });
}

/**
 * Hook to fetch a specific version by ID
 */
export function usePromptVersionById(versionId: string | null) {
  return useQuery<PromptVersionResponse>({
    queryKey: [...VERSIONS_QUERY_KEY, versionId],
    queryFn: async () => {
      const { data } = await api.get<PromptVersionResponse>(
        `/assistants/${MY_IABLUE_ASSISTANT_ID}/prompt/versions/${versionId}`
      );
      return data;
    },
    enabled: !!versionId,
  });
}

/**
 * Hook to publish current draft as a new version
 */
export function usePublishPrompt() {
  const queryClient = useQueryClient();

  return useMutation<PromptVersionResponse, Error>({
    mutationFn: async () => {
      const { data } = await api.post<PromptVersionResponse>(
        `/assistants/${MY_IABLUE_ASSISTANT_ID}/prompt/publish`
      );
      return data;
    },
    onSuccess: () => {
      // Invalidate draft (hasChanges will be false now)
      queryClient.invalidateQueries({ queryKey: PROMPT_DRAFT_QUERY_KEY });
      // Invalidate versions list
      queryClient.invalidateQueries({ queryKey: VERSIONS_QUERY_KEY });
    },
  });
}

/**
 * Hook to restore a version's content to the draft
 */
export function useRestorePromptVersion() {
  const queryClient = useQueryClient();

  return useMutation<PromptDraftResponse, Error, string>({
    mutationFn: async (versionId) => {
      const { data } = await api.post<PromptDraftResponse>(
        `/assistants/${MY_IABLUE_ASSISTANT_ID}/prompt/versions/${versionId}/restore`
      );
      return data;
    },
    onSuccess: (data) => {
      // Update draft cache with restored content
      queryClient.setQueryData(PROMPT_DRAFT_QUERY_KEY, data);
    },
  });
}

/**
 * Hook to fetch prompt preview (shows full prompt with custom + default sections)
 */
export function usePromptPreview(source: 'draft' | 'active' = 'draft', enabled: boolean = true) {
  return useQuery<PromptPreviewResponse>({
    queryKey: ['prompt-preview', MY_IABLUE_ASSISTANT_ID, source],
    queryFn: async () => {
      const { data } = await api.get<PromptPreviewResponse>(
        `/assistants/${MY_IABLUE_ASSISTANT_ID}/prompt/preview`,
        { params: { source } }
      );
      return data;
    },
    enabled,
  });
}

/**
 * Query keys exported for cache management
 */
export const PROMPT_VERSIONS_QUERY_KEY = VERSIONS_QUERY_KEY;
